/**
 * Whether an ops click on an alert row would open anything: the drawer it
 * asks for has to be there to open.
 *
 * The click (`handleAlertAction`) navigates to the device's screen and asks
 * it, by `nx:alert-deep-link`, to open the matching drawer. Fleet and Tasks
 * open one only for a robot or a task in their own lists, and do nothing
 * otherwise -- so an alert about a job that has aged out of the recent list
 * into Older tasks, or about a robot Fleet no longer lists (one renamed, as
 * `phantas_01` became NomNom on 2026-10-21), would land on a screen with
 * nothing opened. Such a row is not offered as a link, for the reason a row
 * about no one place is not.
 *
 * Reads the lists those screens read, through the lookups their listeners
 * use (`robotForDeepLink`, `taskForDeepLink`), so the two cannot disagree.
 * A door or lift link always counts: Devices switches to that table even
 * when the name matches no row, which is still the right list.
 */
import { useCallback, useMemo } from 'react';
import { useRobots, useTasks } from '../../api/hooks';
import { useCanSeeSimulated } from '../../auth/useCanSeeSimulated';
import {
  robotForDeepLink,
  taskForDeepLink,
  type AlertDeepLinkKind,
} from '../../state/alert-deep-link';
import { isSimulatedFleet } from '../../state/simulated-fleet';

export function useAlertLinkOpens(): (link: AlertDeepLinkKind) => boolean {
  const robots = useRobots().data;
  const tasks = useTasks().data;
  // Fleet's own list: a site audience never sees the simulated fleet there.
  const showSimulated = useCanSeeSimulated();
  const fleetRobots = useMemo(
    () => (robots ?? []).filter((r) => showSimulated || !isSimulatedFleet(r.fleet)),
    [robots, showSimulated],
  );
  return useCallback(
    (link: AlertDeepLinkKind) => {
      switch (link.kind) {
        case 'robot':
          return robotForDeepLink(fleetRobots, link.robotName) !== undefined;
        case 'task':
          return taskForDeepLink(tasks ?? [], link.taskId) !== undefined;
        default:
          return true;
      }
    },
    [fleetRobots, tasks],
  );
}
